import React, { useRef } from 'react'
import { AppForms, variantProvider } from '@/app'
import { AppStatus, useAppSelector } from '@/redux'
import { APIClient, analytics } from '@/services'
import { objectPickBy, TypeGuards, useForm, useI18N, useModal } from '@codeleap/common'
import { Text, View, Select, Button } from '@/components'
import { useKeydown } from '@/utils'
import { navigate } from 'gatsby'
import { Organisation } from '@/features'

type AboutJobFormProps = {
  isModal?: boolean
}

export const AboutJobForm = ({ isModal = true }: AboutJobFormProps) => {
  const { data: profile } = APIClient.Session.useProfile()
  const isFocused = useAppSelector(state => state?.AppStatus?.modals?.aboutJob)
  const { data: options } = APIClient.Session.useProfileOptions()
  const reauthenticationModal = useModal()
  const { t } = useI18N()

  const organisationSelectRef = useRef(null)

  const initialState = React.useMemo(
    () => ({
      ...profile,
      organization: !!profile?.organization
        ? {
          label: profile?.organization?.name,
          value: profile?.organization?.id,
          item: profile?.organization,
          ...profile?.organization,
        }
        : null,
    }),
    [profile],
  )

  const { editProfile } = APIClient.Session.useEdit()
  const form = useForm(AppForms.aboutJob, {
    initialState,
  })

  const onboarding = Organisation.Hooks.useOnboarding()

  const onSave = async () => {
    const { organization, department, seniority } = form.values

    const data = {
      organization: TypeGuards.isNumber(organization) ? organization : organization?.id,
      department,
      seniority,
    }

    analytics.track('work_info_start', {
      org_name: form.values.organization,
      function: form.values.department,
      seniority: form.values.seniority,
    })

    try {
      const serializedData = objectPickBy(data, value => !TypeGuards.isNil(value))
      await editProfile(serializedData)
    } catch (e) {
      if (e === APIClient.Session.ERRORS.reautenticationRequired) {
        reauthenticationModal.toggle()
        return
      }
    }

    if (isModal) {
      onboarding.next()
    } else {
      AppStatus.setModal(['profileRedirect', false])
      navigate('/')
    }
  }

  useKeydown(
    () => {
      const isInvalidSubmit = (isModal && !isFocused) || !form.isValid
      if (isInvalidSubmit) return
      onSave?.()
    },
    [isFocused, isModal, form?.values],
    'enter',
  )

  return (
    <View variants={['flex', 'column']}>
      <Text variants={['h1', 'extraBold', 'textCenter', 'marginBottom:4']} text={t('AboutJob.title')} />
      <Text variants={['p1', 'marginBottom:4', 'marginTop:-3', 'textCenter']} text={t('AboutJob.sub')} />

      <View variants={['column', 'gap:3', 'center']}>
        <View variants={['column', 'gap:1', 'fullWidth']}>
          <Organisation.OrganisationSelect
            ref={organisationSelectRef}
            loadInitialValue
            {...form.register('organization')}
            variants={['fullWidth']}
          />
          <Select
            loadInitialValue
            {...form.register('department')}
            options={options?.department}
            variants={['fullWidth']}
          />
          <Select
            loadInitialValue
            {...form.register('seniority')}
            options={options?.seniority}
            variants={['fullWidth']}
            style={{ width: '50%' }}
          />
        </View>

        <View variants={['row', 'justifySpaceBetween']} style={styles.footer}>
          <Button
            text={t('AboutJob.save')}
            disabled={!form.isValid}
            debugName={'Save Button'}
            variants={['pill', 'large', 'neutral9', 'fullWidth', !form.isValid && 'outline']}
            onPress={onSave}
          />
        </View>
      </View>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    footer: {
      width: '50%',

      [theme.media.down('small')]: {
        ...theme.presets.fullWidth,
      },
    },
  }),
  true,
)
